import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/buttons/Button';
import BackButton from '../components/buttons/BackButton';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import { isCapacitor, isIOS } from '../utils/platform';
import { subscriptionService, authManager } from '../services';
import { iOSPurchaseService } from '../services/iOSPurchaseService';
import {
  FaCrown,
  FaBuilding,
  FaSignOutAlt,
  FaInfoCircle,
  FaTags,
  FaCheck,
  FaTimes,
  FaSync,
  FaGraduationCap,
  FaGift,
  FaCopy
} from 'react-icons/fa';
import './Subscription.css';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../services/Debug';
import { openExternalUrl, onBrowserClosed } from '../utils/browserHelper';

function SubscriptionPlans({ handleLogout }) {
  const log = createLogger('SubscriptionPlans');
  const [isLoading, setIsLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [showFellowModal, setShowFellowModal] = useState(false);
  const [isFellow, setIsFellow] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRestoringPurchases, setIsRestoringPurchases] = useState(false);
  const [iOSPurchaseError, setiOSPurchaseError] = useState('');

  const navigate = useNavigate();
  const { profile, refreshProfile } = useAuth();

  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Check localStorage for previous Fellow modal response
  useEffect(() => {
    // Never show Fellow modal on iOS
    if (isIOS) {
      setShowFellowModal(false);
      setIsFellow(false);
      return;
    }

    const hasAnswered = localStorage.getItem('voxia_fellow_modal_answered');

    if (hasAnswered !== null) {
      // User has answered before - don't show modal
      setShowFellowModal(false);
      setIsFellow(hasAnswered === 'true');
    } else {
      // User hasn't answered before - show modal
      setShowFellowModal(true);
    }
  }, []);

  // Listen for browser close events (when Stripe Checkout closes)
  useEffect(() => {
    const cleanup = onBrowserClosed(async () => {
      log.info('Browser closed, refreshing profile and reloading page');
      try {
        await refreshProfile();
        // Reload the page to re-evaluate routing guards and subscription status
        window.location.reload();
      } catch (error) {
        log.error('Error refreshing after browser close:', error);
        // Still reload even if refresh fails
        window.location.reload();
      }
    });

    return cleanup;
  }, []);

  // Auto-navigate to home when subscription becomes active
  useEffect(() => {
    const isActive = profile?.isSubscriptionActive === true || profile?.subscription?.status === 'active';
    if (isActive) {
      log.info('Subscription is now active, navigating to home');
      navigate('/home', { replace: true });
    }
  }, [profile, navigate]);

  // Check if user is eligible for FELLOW50 promo
  const isEligibleForFellow50 = () => {
    if (!profile?.medicalInfo) return false;
    
    const title = profile.medicalInfo.title?.toLowerCase() || '';
    const hasFMH = profile.medicalInfo.hasFMH === true;
    
    // Eligible if: médecin assistant, stagiaire, or CDC without FMH
    const isMedecinAssistant = title.includes('médecin assistant') || title.includes('assistant');
    const isStagiaire = title.includes('stagiaire');
    const isCDC = title.includes('cdc') || title.includes('chef de clinique');
    
    return (isMedecinAssistant || isStagiaire || (isCDC && !hasFMH));
  };

  // Handle fellow response
  const handleFellowResponse = (answer) => {
    setIsFellow(answer);
    setShowFellowModal(false);
    // Save to localStorage so modal won't show again
    localStorage.setItem('voxia_fellow_modal_answered', String(answer));
  };

  // Copy promo code to clipboard
  const handleCopyPromoCode = () => {
    navigator.clipboard.writeText('FELLOW50');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Plans data with base prices and discounted prices
  const allPlans = [
    {
      id: 'bronze',
      name: 'Bronze',
      basePrice: 39.90,
      price: 29.90,
      currency: 'CHF',
      interval: 'month',
      icon: '/abo_bronze.png',
      color: '#CD7F32',
      features: [
        { text: '10 rapports par jour', highlight: false },
        { text: 'Scanner des documents en annexe', highlight: false },
        { text: 'Support par e-mail / FAQ', highlight: false },
        { text: 'Historique limité à 7 jours', highlight: false },
        { text: 'Optimisation constante selon vos préférences', highlight: false, hidden: true },
        { text: 'Dictez et recevez dans votre langue', highlight: false, hidden: true }
      ],
      limitations: [
        'Pas d\'assistance personnalisée',
        'Pas de formats d\'export'
      ],
      recommended: false
    },
    {
      id: 'silver',
      name: isIOS ? 'Mobile' : 'Silver',
      basePrice: 69.90,
      price: 49.90,
      currency: 'CHF',
      interval: 'month',
      icon: '/abo_silver.png',
      color: '#C0C0C0',
      features: [
        { text: '25 rapports par jour', highlight: true },
        { text: 'Scanner des documents en annexe', highlight: false },
        { text: 'Application bureau dédiée', highlight: true },
        { text: 'Support par e-mail / FAQ', highlight: false },
        { text: 'Formats multiples (PDF, Word, e-mail)', highlight: true },
        { text: 'Historique complet avec recherche', highlight: true },
        { text: 'Optimisation constante selon vos préférences', highlight: false, hidden: true },
        { text: 'Dictez dans une langue, recevez dans une autre', highlight: true, hidden: true }
      ],
      limitations: [],
      recommended: true
    },
    {
      id: 'gold',
      name: 'Gold',
      basePrice: 139.90,
      price: 99.90,
      currency: 'CHF',
      interval: 'month',
      icon: '/abo_gold.png',
      color: '#FFD700',
      features: [
        { text: '100 rapports par jour', highlight: true },
        { text: 'Scanner des documents en annexe', highlight: false },
        { text: 'Application bureau dédiée', highlight: false },
        { text: 'Dictées lives', highlight: true },
        { text: 'Création de modèles de rapport', highlight: true },
        { text: 'Assistance personnalisée pour lettres sur-mesure', highlight: true },
        { text: 'Support par e-mail / FAQ / assistance personnalisée', highlight: true },
        { text: 'Formats multiples (PDF, Word, e-mail)', highlight: false },
        { text: 'Historique complet avec recherche', highlight: false },
        { text: 'Optimisation constante selon vos préférences', highlight: false, hidden: true },
        { text: 'Dictez dans une langue, recevez dans une autre', highlight: false, hidden: true },
        { text: 'Créer une organisation', highlight: false, hidden: true }
      ],
      limitations: [],
      recommended: false
    }
  ];

  // Filter plans based on platform - only show Silver (Mobile) on iOS
  const plans = isIOS ? allPlans.filter(plan => plan.id === 'silver') : allPlans;

  // Calculate final price based on promo code
  const calculateFinalPrice = (plan) => {
    if (!promoDiscount || !promoCode) return plan.price;
    
    // Check if promo code ends with 2 digits
    const last2Chars = promoCode.slice(-2);
    const isNumeric = /^\d{2}$/.test(last2Chars);
    
    let finalPrice = plan.price;
    
    if (isNumeric) {
      // If code ends with 2 numbers, use them as percentage discount
      const percent = parseInt(last2Chars);
      finalPrice = plan.price * (1 - percent / 100);
    } else {
      // Fallback: Parse promo discount message (e.g., "10% de réduction" or "5 CHF de réduction")
      const percentMatch = promoDiscount.match(/(\d+)%/);
      const amountMatch = promoDiscount.match(/(\d+)\s*CHF/);
      
      if (percentMatch) {
        const percent = parseInt(percentMatch[1]);
        finalPrice = plan.price * (1 - percent / 100);
      } else if (amountMatch) {
        const amount = parseInt(amountMatch[1]);
        finalPrice = Math.max(0, plan.price - amount);
      }
    }
    
    return finalPrice;
  };

  // Calculate discount percentage for display
  const getDiscountPercentage = (plan) => {
    const finalPrice = calculateFinalPrice(plan);
    return Math.round(((plan.basePrice - finalPrice) / plan.basePrice) * 100);
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      setPromoError('');
      setPromoDiscount(null);
      return;
    }

    setIsValidatingPromo(true);
    setPromoError('');

    try {
      const response = await subscriptionService.validatePromoCode(promoCode);
      if (response.valid) {
        setPromoDiscount(response.message);
        setPromoError('');
      } else {
        setPromoError('Code promo invalide');
        setPromoDiscount(null);
      }
    } catch (error) {
      setPromoError('Code promo invalide');
      setPromoDiscount(null);
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const handleSelectPlan = async (plan) => {
    setSelectedPlan(plan);
    setIsProcessing(true);
    setiOSPurchaseError('');

    // Safety timeout for iOS purchases (declared here to be accessible in catch)
    let purchaseTimeout = null;

    try {
      // On iOS, use in-app purchase instead of Stripe
      if (isIOS) {
        // Only Silver (Mobile) plan is supported on iOS
        if (plan.id !== 'silver') {
          alert('Sur iOS, seul l\'abonnement Mobile mensuel est disponible via l\'App Store. Les autres plans sont disponibles sur le web.');
          setIsProcessing(false);
          return;
        }

        log.info('Initiating iOS in-app purchase');

        // Safety timeout: reset isProcessing after 30 seconds if callbacks don't fire
        // (e.g., user dismisses StoreKit dialog without completing)
        purchaseTimeout = setTimeout(() => {
          log.warn('iOS purchase timeout - resetting processing state');
          setIsProcessing(false);
        }, 30000);

        // Helper to clear timeout when callbacks fire
        const clearPurchaseTimeout = () => {
          if (purchaseTimeout) clearTimeout(purchaseTimeout);
        };

        // Initialize iOS purchase service if needed
        await iOSPurchaseService.initialize();

        // Initiate the purchase
        await iOSPurchaseService.initiatePurchase(
          // Success callback - called after transaction is linked
          async (result) => {
            clearPurchaseTimeout();
            log.info('iOS purchase linked:', result);

            // Transaction is linked, Apple webhook will activate the subscription
            // Poll for subscription activation (webhook may take a moment)
            let activated = false;
            for (let attempt = 1; attempt <= 5; attempt++) {
              log.info(`Checking subscription status, attempt ${attempt}...`);
              await refreshProfile();

              // Small delay to allow state to update
              await new Promise(resolve => setTimeout(resolve, 500));

              // Check if subscription is now active via profile
              const currentProfile = await refreshProfile();
              if (currentProfile?.isSubscriptionActive || currentProfile?.subscription?.status === 'active') {
                log.info('Subscription activated!');
                activated = true;
                break;
              }

              // Wait before next attempt
              if (attempt < 5) {
                await new Promise(resolve => setTimeout(resolve, 1500));
              }
            }

            if (activated) {
              // Navigate to home - the useEffect will handle this
              // but let's also force a profile refresh
              await refreshProfile();
            } else {
              // Webhook hasn't processed yet, but purchase was successful
              log.info('Transaction linked but subscription not yet active - webhook may still be processing');
              setiOSPurchaseError('Achat réussi ! Votre abonnement sera activé dans quelques instants. Cliquez sur "Actualiser mon statut".');
              setIsProcessing(false);
            }
          },
          // Error callback
          (error) => {
            clearPurchaseTimeout();
            log.error('iOS purchase failed:', error);
            const errorMessage = error?.message || 'Erreur lors de l\'achat';

            // Check for user cancellation
            if (errorMessage.includes('cancel') || errorMessage.includes('Cancel')) {
              setiOSPurchaseError('Achat annulé');
            } else if (errorMessage.includes('verification failed') || errorMessage.includes('not active')) {
              // Purchase succeeded but backend verification failed
              // The transaction will be retried by Apple, suggest restore purchases
              setiOSPurchaseError('Achat effectué mais vérification en attente. Essayez "Restaurer mes achats" dans quelques instants.');
            } else if (errorMessage.includes('receipt') || errorMessage.includes('Receipt')) {
              // Receipt retrieval issue
              setiOSPurchaseError('Impossible de récupérer le reçu. Essayez "Restaurer mes achats".');
            } else {
              setiOSPurchaseError(errorMessage);
            }

            setIsProcessing(false);
          }
        );
      } else {
        // Web/Android/Electron: Use Stripe Checkout
        const response = await subscriptionService.createCheckoutSession(plan.id, promoDiscount ? promoCode : undefined);

        if (response.url) {
          // Open Stripe Checkout (in-app browser on mobile, redirect on web)
          await openExternalUrl(response.url);
        }

        setIsProcessing(false);
      }
    } catch (error) {
      log.error('Error in handleSelectPlan:', error);

      // Clear timeout if set
      if (purchaseTimeout) clearTimeout(purchaseTimeout);

      if (isIOS) {
        setiOSPurchaseError('Erreur lors de l\'achat. Veuillez réessayer.');
      } else {
        alert('Erreur lors de la création de la session de paiement');
      }

      setIsProcessing(false);
    }
  };

  const onLogout = handleLogout || (async () => {
    try {
      await authManager.logout();
    } catch (_) {}
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshProfile();
      // The useEffect watching profile will auto-navigate if subscription is now active
    } catch (_) {
      // ignore
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRestorePurchases = async () => {
    setIsRestoringPurchases(true);
    setiOSPurchaseError('');

    try {
      log.info('Restoring iOS purchases');

      // Initialize iOS purchase service if needed
      await iOSPurchaseService.initialize();

      // Restore purchases
      await iOSPurchaseService.restorePurchases(
        // Success callback
        async (subscription) => {
          log.info('iOS purchase restored successfully:', subscription);

          // Refresh profile to update subscription status
          await refreshProfile();

          // Show success message
          alert('Abonnement restauré avec succès !');

          // Reload the page
          window.location.reload();
        },
        // Error callback
        (error) => {
          log.error('iOS restore failed:', error);
          const errorMessage = error?.message || 'Aucun achat précédent trouvé';

          if (errorMessage.includes('No previous purchases') || errorMessage.includes('No active subscription')) {
            setiOSPurchaseError('Aucun abonnement actif trouvé');
          } else {
            setiOSPurchaseError(errorMessage);
          }

          setIsRestoringPurchases(false);
        }
      );
    } catch (error) {
      log.error('Error restoring purchases:', error);
      setiOSPurchaseError('Erreur lors de la restauration. Veuillez réessayer.');
      setIsRestoringPurchases(false);
    }
  };

  if (isLoading) {
    return (
      <div className="subscription-container">
        <div className="loading-message">Chargement des plans...</div>
      </div>
    );
  }

  return (
    <>
      {/* Fellow Modal */}
      {showFellowModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'var(--gradient-primary)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <FaGraduationCap style={{ color: '#fff', fontSize: '40px' }} />
              </div>
              <h2 style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: 'var(--color-dark)',
                margin: '0 0 12px 0'
              }}>
                Êtes-vous un Fellow ?
              </h2>
              <p style={{ 
                fontSize: '16px', 
                color: '#666', 
                lineHeight: '1.6',
                margin: '0 0 12px 0'
              }}>
                Les médecins assistants, stagiaires et CDC sans FMH<br />
                bénéficient d'une réduction de 50% supplémentaire !
              </p>
              
              <div style={{
                background: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '8px',
                padding: '10px 12px',
                marginBottom: '16px'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#856404',
                  margin: 0,
                  lineHeight: '1.5'
                }}>
                  <FaInfoCircle style={{ marginRight: '6px', fontSize: '12px' }} />
                  Offre basée sur la confiance. Des contrôles seront effectués et l'abonnement sera annulé en cas de déclaration non conforme.
                </p>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px',
              flexDirection: isMobile ? 'column' : 'row'
            }}>
              <Button
                variant="primary"
                onClick={() => handleFellowResponse(true)}
                style={{
                  flex: 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  padding: '14px 24px'
                }}
              >
                <FaCheck style={{ marginRight: '8px' }} />
                Oui, je suis Fellow
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleFellowResponse(false)}
                style={{
                  flex: 1,
                  fontSize: '16px',
                  fontWeight: '600',
                  padding: '14px 24px'
                }}
              >
                Non
              </Button>
            </div>
          </div>
        </div>
      )}

      {isCapacitor && (
        <div className="capacitor-topbar">
          <div className="topbar-content">
            <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="Logo" className="topbar-logo" />
            <h2 className="topbar-title">voxia</h2>
          </div>
        </div>
      )}

      <div className="subscription-container">
       {profile?.isSubscriptionActive && <BackButton to="/home" />}
        
        {/* Header */}
        <Card>
          <SectionHeader
            icon={FaCrown}
            title={isIOS ? "Choisissez votre abonnement" : "Choisissez votre plan"}
          />
          <p style={{ textAlign: 'center', color: '#666', marginTop: '10px', fontSize: '16px' }}>
            Commencez avec 30 jours d'essai gratuit. Annulez à tout moment.
          </p>
          
          {/* Special FELLOW50 Banner - Hidden on iOS */}
          {isFellow && !isIOS && (
            <div style={{
              marginTop: '20px',
              background: 'var(--gradient-primary)',
              borderRadius: '10px',
              padding: '16px',
              border: '2px solid #b30020',
              boxShadow: '0 4px 16px rgba(150, 0, 24, 0.25)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: -15,
                right: -15,
                width: 80,
                height: 80,
                background: 'rgba(255, 255, 255, 0.08)',
                borderRadius: '50%'
              }} />
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: '200px' }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <FaGraduationCap style={{ color: '#fff', fontSize: '18px' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ 
                      margin: 0, 
                      color: '#fff', 
                      fontSize: '15px', 
                      fontWeight: '700',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <FaGift style={{ fontSize: '14px' }} />
                      Offre Fellow
                    </h3>
                    <p style={{ margin: '2px 0 0 0', color: 'rgba(255, 255, 255, 0.9)', fontSize: '12px' }}>
                      -50% supplémentaire
                    </p>
                  </div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '800', 
                    color: '#fff',
                    letterSpacing: '1.5px',
                    fontFamily: 'monospace'
                  }}>
                    FELLOW50
                  </div>
                  <button
                    onClick={handleCopyPromoCode}
                    style={{
                      background: copied ? '#4ade80' : '#fff',
                      color: copied ? '#fff' : 'var(--color-primary)',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                      transform: copied ? 'scale(1.05)' : 'scale(1)'
                    }}
                    onMouseEnter={(e) => {
                      if (!copied) {
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!copied) {
                        e.currentTarget.style.transform = 'scale(1)';
                      }
                    }}
                  >
                    {copied ? <FaCheck /> : <FaCopy />}
                    {copied ? 'Copié !' : 'Copier'}
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* iOS Error Display (if any) */}
          {isIOS && iOSPurchaseError && (
            <div style={{
              marginTop: '20px',
              background: '#ffebee',
              borderRadius: '10px',
              padding: '16px',
              border: '1px solid #ef5350',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaInfoCircle style={{ color: '#d32f2f', fontSize: '20px', flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: '14px', color: '#d32f2f', fontWeight: '600' }}>
                {iOSPurchaseError}
              </div>
            </div>
          )}

          {/* Promo Code Section - Hidden on iOS */}
          {!isIOS && (
            <div style={{ marginTop: '30px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px', justifyContent: 'center' }}>
                <FaTags style={{ color: 'var(--color-gray)', fontSize: '18px' }} />
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-dark)' }}>Code promotionnel</h3>
              </div>
              <div className="promo-section-inner">
                <div className="promo-input-wrapper">
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="text"
                        placeholder="Entrez votre code promo (optionnel)"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            validatePromoCode();
                          }
                        }}
                        className="promo-input"
                        style={{
                          borderColor: promoError ? 'var(--color-danger)' : promoDiscount ? 'var(--color-success)' : '#e9ecef'
                        }}
                      />
                    </div>
                    <Button
                      variant="primary"
                      onClick={validatePromoCode}
                      disabled={isValidatingPromo || !promoCode.trim()}
                      style={{
                        padding: '10px 20px',
                        fontSize: '14px',
                        fontWeight: '600',
                        minWidth: '100px'
                      }}
                    >
                      {isValidatingPromo ? 'Validation...' : 'Valider'}
                    </Button>
                  </div>
                  {promoDiscount && (
                    <span className="promo-valid" style={{ marginTop: '10px', display: 'inline-block' }}>
                      <FaCheck style={{ marginRight: '5px' }} />
                      {promoDiscount}
                    </span>
                  )}
                  {promoError && (
                    <span className="promo-error" style={{ marginTop: '10px', display: 'inline-block' }}>
                      <FaTimes style={{ marginRight: '5px' }} />
                      {promoError}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Plans Comparison - Mobile */}
        {isMobile ? (
          <Card className="card-large">
            <div className="plans-mobile-list" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {plans.map((plan) => (
                <div 
                  key={plan.id} 
                  className="plan-card-mobile" 
                  style={{ 
                    border: plan.recommended ? `2px solid ${plan.color}` : '1px solid #eee', 
                    borderRadius: 16, 
                    padding: 20,
                    position: 'relative',
                    background: plan.recommended ? `${plan.color}05` : 'white'
                  }}
                >
                  {plan.recommended && !isIOS && (
                    <div
                      style={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: plan.color,
                        color: '#fff',
                        borderRadius: 20,
                        padding: '6px 16px',
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}
                    >
                      Plus populaire
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                    <div style={{ 
                      width: 64, 
                      height: 64, 
                      borderRadius: 12, 
                      backgroundColor: `${plan.color}20`, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <img src={plan.icon} alt={plan.name} style={{ maxWidth: 48, maxHeight: 48 }} />
                    </div>
                  </div>
                  
                  <h3 style={{ margin: '0 0 12px 0', color: plan.color, textAlign: 'center', fontSize: 24, fontWeight: 700 }}>
                    {plan.name}
                  </h3>
                  
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ background: '#ffe0e0', color: '#c62828', borderRadius: 8, padding: '4px 8px', fontSize: 12, fontWeight: 600 }}>
                        -{getDiscountPercentage(plan)}%
                      </span>
                    </div>
                    
                    <div style={{ marginBottom: 4 }}>
                      <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: 14 }}>
                        {plan.basePrice} {plan.currency}
                      </span>
                    </div>
                    
                    {promoDiscount && calculateFinalPrice(plan) < plan.price && (
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ textDecoration: 'line-through', opacity: 0.5, fontSize: 16, color: '#999' }}>
                          {plan.price.toFixed(2)} {plan.currency}
                        </span>
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 32, fontWeight: 700, color: 'var(--color-dark)' }}>
                        {calculateFinalPrice(plan).toFixed(2)}
                      </span>
                      <span style={{ fontSize: 16, color: '#666' }}>{plan.currency}</span>
                      <span style={{ fontSize: 14, color: '#666' }}>/ {plan.interval === 'month' ? 'mois' : 'an'}</span>
                    </div>
                    
                    {promoDiscount && calculateFinalPrice(plan) < plan.price && (
                      <div style={{ marginTop: 8 }}>
                        <span style={{ 
                          background: '#e8f5e9', 
                          color: '#2e7d32', 
                          borderRadius: 8, 
                          padding: '4px 10px', 
                          fontSize: 12, 
                          fontWeight: 600 
                        }}>
                          Code promo appliqué !
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div style={{ marginBottom: 20 }}>
                    <ul style={{ margin: 0, padding: 0, display: 'grid', gap: 10 }}>
                      {plan.features.filter(f => !f.hidden || showAllFeatures).map((feature, idx) => (
                        <li key={idx} style={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <FaCheck style={{ color: plan.color, flexShrink: 0, marginTop: 2, fontSize: 14 }} />
                          <span style={{ fontSize: 14, fontWeight: feature.highlight ? 600 : 400 }}>
                            {feature.text}
                          </span>
                        </li>
                      ))}
                      {showAllFeatures && plan.limitations.map((limitation, idx) => (
                        <li key={idx} style={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <FaTimes style={{ color: '#ccc', flexShrink: 0, marginTop: 2, fontSize: 14 }} />
                          <span style={{ fontSize: 14, color: '#999' }}>
                            {limitation}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    {plan.features.filter(f => f.hidden).length > 0 && (
                      <div style={{ textAlign: 'center', marginTop: 12 }}>
                        <button
                          onClick={() => setShowAllFeatures(!showAllFeatures)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: plan.color,
                            fontSize: 14,
                            fontWeight: 500,
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          {showAllFeatures ? 'Voir moins' : 'Voir plus'}
                        </button>
                      </div>
                    )}
                  </div>
                  
                  <Button
                    variant="primary"
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isProcessing}
                    style={{
                      width: '100%',
                      background: plan.id === 'bronze'
                        ? 'linear-gradient(135deg, #8B5A2B 0%, #6B4423 100%)'
                        : plan.id === 'silver'
                        ? 'linear-gradient(135deg, #404040 0%, #2a2a2a 100%)'
                        : 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                      border: `3px solid ${plan.color}`,
                      fontSize: '18px',
                      fontWeight: 800,
                      padding: '18px 32px',
                      boxShadow: plan.id === 'gold'
                        ? `0 8px 24px rgba(184, 134, 11, 0.4), 0 0 0 4px rgba(255, 215, 0, 0.15)`
                        : `0 8px 24px ${plan.color}70, 0 0 0 4px ${plan.color}20`,
                      transition: 'all 0.3s ease',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      color: '#ffffff',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05) translateY(-3px)';
                      if (plan.id === 'gold') {
                        e.currentTarget.style.boxShadow = `0 12px 32px rgba(184, 134, 11, 0.6), 0 0 0 6px rgba(255, 215, 0, 0.25)`;
                      } else {
                        e.currentTarget.style.boxShadow = `0 12px 32px ${plan.color}90, 0 0 0 6px ${plan.color}30`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1) translateY(0)';
                      if (plan.id === 'gold') {
                        e.currentTarget.style.boxShadow = `0 8px 24px rgba(184, 134, 11, 0.4), 0 0 0 4px rgba(255, 215, 0, 0.15)`;
                      } else {
                        e.currentTarget.style.boxShadow = `0 8px 24px ${plan.color}70, 0 0 0 4px ${plan.color}20`;
                      }
                    }}
                  >
                    {isProcessing && selectedPlan?.id === plan.id ? (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FaSync style={{ animation: 'spin 1s linear infinite' }} />
                        Chargement...
                      </span>
                    ) : (
                      <>
                        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          {isIOS ? (
                            <span>Prendre l'abonnement</span>
                          ) : (
                            <>
                              <span style={{ fontSize: '11px', opacity: 0.9, textTransform: 'lowercase', letterSpacing: '0.5px' }}>choisir</span>
                              <span>{plan.name}</span>
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </Card>
        ) : (
          /* Plans Comparison - Desktop */
          <Card className="card-large">
            <div className="plans-table-wrapper">
              <table className="plans-comparison-table">
                <thead>
                  <tr>
                    <th className="feature-column">Fonctionnalités</th>
                    {plans.map((plan) => (
                      <th key={plan.id} className="plan-column">
                        <div className="plan-header">
                          {plan.recommended && !isIOS && (
                            <div className="recommended-badge-table" style={{ backgroundColor: plan.color }}>
                              Plus populaire
                            </div>
                          )}
                          <div className="plan-icon-table" style={{ backgroundColor: `${plan.color}20` }}>
                            <img src={plan.icon} alt={plan.name} />
                          </div>
                          <h3 style={{ color: plan.color }}>{plan.name}</h3>
                          <div className="plan-price-table">
                            <div className="price-discount-badge">-{getDiscountPercentage(plan)}%</div>
                            
                            <div className="price-original">
                              <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>
                                {plan.basePrice} {plan.currency}
                              </span>
                            </div>
                            
                            {promoDiscount && calculateFinalPrice(plan) < plan.price && (
                              <div className="price-original" style={{ marginTop: '2px' }}>
                                <span style={{ textDecoration: 'line-through', opacity: 0.4, fontSize: '0.9em' }}>
                                  {plan.price.toFixed(2)} {plan.currency}
                                </span>
                              </div>
                            )}
                            
                            <span className="price-amount">{calculateFinalPrice(plan).toFixed(2)}</span>
                            <span className="price-currency">{plan.currency}</span>
                            <span className="price-interval">/{plan.interval === 'month' ? 'mois' : 'an'}</span>
                            
                            {promoDiscount && calculateFinalPrice(plan) < plan.price && (
                              <div style={{ 
                                marginTop: '8px',
                                background: '#e8f5e9', 
                                color: '#2e7d32', 
                                borderRadius: 6, 
                                padding: '3px 8px', 
                                fontSize: '11px', 
                                fontWeight: 600 
                              }}>
                                Promo appliquée
                              </div>
                            )}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="feature-name">Nombre de rapports par jour</td>
                    <td className="bronze-cell">10</td>
                    <td className="silver-cell"><strong>30</strong></td>
                    <td className="gold-cell"><strong>100</strong></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Scanner des documents</td>
                    <td className="bronze-cell"><FaCheck style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                    <td className="silver-cell"><FaCheck style={{ color: '#C0C0C0', fontSize: '20px' }} /></td>
                    <td className="gold-cell"><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Application bureau dédiée</td>
                    <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                    <td className="silver-cell"><FaCheck style={{ color: '#C0C0C0', fontSize: '20px' }} /></td>
                    <td className="gold-cell"><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Dictées lives</td>
                    <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                    <td className="silver-cell"><FaTimes style={{ color: '#999', fontSize: '20px' }} /></td>
                    <td className="gold-cell"><strong><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></strong></td>
                  </tr>
                  <tr>
                    <td className="feature-name">Création de modèles de rapport</td>
                    <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                    <td className="silver-cell"><FaTimes style={{ color: '#999', fontSize: '20px' }} /></td>
                    <td className="gold-cell"><strong><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></strong></td>
                  </tr>
                  
                  {showAllFeatures && (
                    <>
                      <tr>
                        <td className="feature-name">Amélioration constante des modèles</td>
                        <td className="bronze-cell"><FaCheck style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                        <td className="silver-cell"><FaCheck style={{ color: '#C0C0C0', fontSize: '20px' }} /></td>
                        <td className="gold-cell"><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Langue de dictée</td>
                        <td className="bronze-cell">Unilingual</td>
                        <td className="silver-cell"><strong>Multilingual</strong></td>
                        <td className="gold-cell"><strong>Multilingual</strong></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Assistance personnalisée lettres sur-mesure</td>
                        <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                        <td className="silver-cell"><FaTimes style={{ color: '#999', fontSize: '20px' }} /></td>
                        <td className="gold-cell"><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Support client</td>
                        <td className="bronze-cell">Par e-mail / FAQ</td>
                        <td className="silver-cell">Par e-mail / FAQ</td>
                        <td className="gold-cell"><strong>Par e-mail / FAQ / assistance personnalisée</strong></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Exporter avec vos templates</td>
                        <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                        <td className="silver-cell"><strong>PDF, Word</strong></td>
                        <td className="gold-cell"><strong>PDF, Word</strong></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Historique des rapports</td>
                        <td className="bronze-cell">Limité à 7 jours</td>
                        <td className="silver-cell"><strong>Complet avec recherche</strong></td>
                        <td className="gold-cell"><strong>Complet avec recherche</strong></td>
                      </tr>
                      <tr>
                        <td className="feature-name">Créer une organisation</td>
                        <td className="bronze-cell"><FaTimes style={{ color: '#CD7F32', fontSize: '20px' }} /></td>
                        <td className="silver-cell"><FaTimes style={{ color: '#999', fontSize: '20px' }} /></td>
                        <td className="gold-cell"><strong><FaCheck style={{ color: '#FFD700', fontSize: '20px' }} /></strong></td>
                      </tr>
                    </>
                  )}
                  
                  <tr style={{ background: '#f8f9fa' }}>
                    <td colSpan="4" style={{ textAlign: 'center', padding: '16px', borderBottom: '1px solid #e9ecef' }}>
                      <Button
                        variant="secondary"
                        onClick={() => setShowAllFeatures(!showAllFeatures)}
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        {showAllFeatures ? 'Voir moins' : 'Voir plus de fonctionnalités'}
                      </Button>
                    </td>
                  </tr>
                  
                  <tr className="action-row">
                    <td className="feature-name"></td>
                    {plans.map((plan) => (
                      <td key={plan.id} style={{ textAlign: 'center', padding: '32px 12px' }}>
                        <Button
                          variant="primary"
                          onClick={() => handleSelectPlan(plan)}
                          disabled={isProcessing}
                          style={{
                            background: plan.id === 'bronze'
                              ? 'linear-gradient(135deg, #8B5A2B 0%, #6B4423 100%)'
                              : plan.id === 'silver'
                              ? 'linear-gradient(135deg, #404040 0%, #2a2a2a 100%)'
                              : 'linear-gradient(135deg, #DAA520 0%, #B8860B 100%)',
                            border: `3px solid ${plan.color}`,
                            minWidth: '200px',
                            fontSize: '16px',
                            fontWeight: 800,
                            padding: '16px 24px',
                            boxShadow: plan.id === 'gold'
                              ? `0 8px 24px rgba(184, 134, 11, 0.4), 0 0 0 4px rgba(255, 215, 0, 0.15)`
                              : `0 8px 24px ${plan.color}70, 0 0 0 4px ${plan.color}20`,
                            transition: 'all 0.3s ease',
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            color: '#ffffff',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05) translateY(-3px)';
                            if (plan.id === 'gold') {
                              e.currentTarget.style.boxShadow = `0 12px 32px rgba(184, 134, 11, 0.6), 0 0 0 6px rgba(255, 215, 0, 0.25)`;
                            } else {
                              e.currentTarget.style.boxShadow = `0 12px 32px ${plan.color}90, 0 0 0 6px ${plan.color}30`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1) translateY(0)';
                            if (plan.id === 'gold') {
                              e.currentTarget.style.boxShadow = `0 8px 24px rgba(184, 134, 11, 0.4), 0 0 0 4px rgba(255, 215, 0, 0.15)`;
                            } else {
                              e.currentTarget.style.boxShadow = `0 8px 24px ${plan.color}70, 0 0 0 4px ${plan.color}20`;
                            }
                          }}
                        >
                          {isProcessing && selectedPlan?.id === plan.id ? (
                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <FaSync style={{ animation: 'spin 1s linear infinite' }} />
                              Chargement...
                            </span>
                          ) : (
                            <>
                              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                {isIOS ? (
                                  <span>Prendre l'abonnement</span>
                                ) : (
                                  <>
                                    <span style={{ fontSize: '10px', opacity: 0.9, textTransform: 'lowercase', letterSpacing: '0.5px' }}>choisir</span>
                                    <span>{plan.name}</span>
                                  </>
                                )}
                              </span>
                            </>
                          )}
                        </Button>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Enterprise Section - Hidden on iOS */}
        {!isIOS && (
          <Card className="card-large">
            <SectionHeader
              icon={FaBuilding}
              title="Plan Entreprise"
            />
            <div style={{ textAlign: 'center' }}>
              <img
                src={`${process.env.PUBLIC_URL}/abo_enterprise.png`}
                alt="Enterprise icon"
                style={{ width: '80px', height: '80px', marginBottom: '20px' }}
              />
              <p style={{ fontSize: '18px', color: '#666', marginBottom: '25px' }}>
                Pour les cliniques et hôpitaux avec des besoins spécifiques
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/contact')}
                icon={FaBuilding}
              >
                Contactez-nous
              </Button>
            </div>
          </Card>
        )}

        {/* Payment Info - Hidden on iOS */}
        {!isIOS && (
          <Card>
            <SectionHeader
              icon={FaInfoCircle}
              title="Informations de paiement"
              variant="secondary"
            />
            <div className="payment-info-content">
              <p>
                <strong>Paiement sécurisé</strong> via Stripe.
                Vos informations de paiement ne sont jamais stockées sur nos serveurs.
              </p>
              <p>
                <strong>Garantie satisfait ou remboursé</strong> pendant les 30 premiers jours.
              </p>
            </div>
          </Card>
        )}

        {/* Actions Section */}
        <Card>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{ minWidth: '200px' }}
            >
              {isRefreshing ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaSync style={{ animation: 'spin 1s linear infinite' }} />
                  Vérification...
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaSync />
                  Actualiser mon statut
                </span>
              )}
            </Button>

            {isIOS && (
              <Button
                variant="primary"
                onClick={handleRestorePurchases}
                disabled={isRestoringPurchases}
                style={{ minWidth: '200px' }}
              >
                {isRestoringPurchases ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSync style={{ animation: 'spin 1s linear infinite' }} />
                    Restauration...
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaSync />
                    Restaurer mes achats
                  </span>
                )}
              </Button>
            )}

            <Button
              variant="danger"
              onClick={onLogout}
              icon={FaSignOutAlt}
              style={{ minWidth: '200px' }}
            >
              Se déconnecter
            </Button>
          </div>
        </Card>
      </div>
    </>
  );
}

export default SubscriptionPlans;
